import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Label } from "@/components/ui/label";
import { Upload } from 'lucide-react';
import { SnapAPicComponent } from './SnapAPicComponent';

interface ReceiptUploadProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

export const ReceiptUpload = ({
  onFilesSelected,
  disabled = false
}: ReceiptUploadProps) => {
  const [dropError, setDropError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setDropError(null);

    if (rejectedFiles.length > 0) {
      const error = rejectedFiles[0].errors[0];
      if (error.code === 'file-too-large') {
        setDropError('One or more files are too large. Maximum size is 10MB.');
      } else if (error.code === 'file-invalid-type') {
        setDropError('Invalid file type. Please upload images or PDFs.');
      } else {
        setDropError('Failed to upload file(s). Please try again.');
      }
    }

    if (acceptedFiles.length > 0) {
      onFilesSelected(acceptedFiles);
    }
  }, [onFilesSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true,
    disabled,
    onDrop
  });

  const handleCameraCapture = useCallback((file: File) => {
    onFilesSelected([file]);
  }, [onFilesSelected]);

  return (
    <div>
      <Label>Upload Receipts</Label>
      <div className="mt-2 flex gap-3 items-stretch">
        {/* Snap A Picture */}
        <div className="flex-1 flex">
          <SnapAPicComponent
            onCapture={handleCameraCapture}
            disabled={disabled}
          />
        </div>

        {/* Upload Files */}
        <div className="flex-1 flex">
          <div
            {...getRootProps()}
            className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center h-full">
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? (
                  'Drop files here...'
                ) : (
                  <>
                    <span className="text-primary">Browse</span> or drag
                  </>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, GIF, PDF (Max 10MB)
              </p>
            </div>
          </div>
        </div>
      </div>
      {dropError && (
        <p className="text-sm text-destructive mt-2">{dropError}</p>
      )}
    </div>
  );
};
